/* eslint-disable complexity */
import React, { useEffect, useState, useRef } from 'react';
import useModelEvalCard from './useModelEvalCard';
import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import { TextArea, TextField, Typography, Button, Icon, Table, BaseIcon } from '@scale/scaleui';
import {
  ChatApiEndpointWithId,
  MessageRole,
  ProjectChatApiEndpointWithId,
} from '@scale/shared/types/chat';
import ChatApiEndpointsBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ChatApiEndpointsBuilder';
import {
  getPopulatedPrompt,
  getPopulatedPromptVariablesAsArray,
  getPopulatedJsonPrompt,
  extractVariablesFromJson,
  validateJsonPrompt,
} from '@scale/genai-compass-shared/cards/model_eval/lib';
import type { Message } from '@scale/compass-shared/llms';
import { produce } from 'immer';
import apiFetch from 'react-scale/lib/api-fetch';
import {
  StrategyParamsSchemaMap,
  masterEndpointMap,
  CreateCompletionRequest,
  CreateCompletionResponse,
} from '@scale/shared/types/chat-completion';
import _ from 'lodash';
import {
  Badge,
  Callout,
  Em,
  Link,
  Strong,
  Text,
  Code as RadixCode,
  Flex,
  Box,
  Checkbox,
  Tooltip as RadixTooltip,
  Icon as RadixIcon,
  SegmentedControl,
} from '@scale/scaleui-radix';
import { faBookOpen } from '@fortawesome/pro-solid-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { CompassInstructions } from '@scale/compass-ui/components/CardInstructions';

import {
  MODEL_EVAL_DEFAULT_NUM_RETRIES,
  MODEL_EVAL_DEFAULT_TIMEOUT_SECONDS,
} from '@scale/genai-compass-shared/cards/model_eval/constants';
import type { EvalConfig } from '@scale/genai-compass-shared/cards/model_eval/card';
import { ModelInputFileAttachmentType } from '@scale/shared/types/chat/response';

import { populateTemplateHelper } from '@scale/shared/taskTemplate/helpers';
const DEFAULT_TEST_PROMPT = 'This is a test message. What are you?';

const DEFAULT_ENDPOINTS: ChatApiEndpointWithId[] = [
  {
    id: 'endpoint_LiteLLMStrategy_1',
    url: 'https://litellm.ml-serving-internal.scale.com/v1/chat/completions',
    params: {
      model: 'gpt-4o-mini',
      provider: 'openai',
    },
  },
];

const JSON_PROMPT_EXAMPLE = `[
  {
    "role": "system",
    "content": "You are a helpful assistant. Use the following context: {{context}}"
  },
  {
    "role": "user",
    "content": "{{question}}"
  }
]`;

interface PreviewResponse {
  outputColumn: string;
  modelResponse: string;
}

interface PreviewResult {
  responses: PreviewResponse[];
  prompt: string;
}

const ModelEvalCardComponent: CompassCardComponent = ({ index }: CompassCardComponentProps) => {
  const {
    evalConfigs,
    setEvalConfigs,
    projectId,
    setProjectId,
    firstDataRow,
    availableVariables,
    hasError,
    useCache,
    setUseCache,
  } = useModelEvalCard(index);

  const [testPrompt, setTestPrompt] = useState(DEFAULT_TEST_PROMPT);
  const [previewResults, setPreviewResults] = useState<{
    [key: number]: PreviewResult;
  }>({});
  const [needsRerunPreview, setNeedsRerunPreview] = useState<{
    [key: number]: boolean;
  }>({});
  const [hasPreviewRun, setHasPreviewRun] = useState<{
    [key: number]: boolean;
  }>({});
  const [isLoading, setIsLoading] = useState<{ [key: number]: boolean }>({});
  const [isPreviewVisible, setIsPreviewVisible] = useState<{
    [key: number]: boolean;
  }>({});

  // Store last cursor positions when fields lose focus
  const lastPromptTextCursorPositions = useRef<{
    [key: number]: { start: number; end: number };
  }>({});
  const lastImageUrlCursorPositions = useRef<{
    [key: number]: { start: number; end: number };
  }>({});
  const lastAudioUrlCursorPositions = useRef<{
    [key: number]: { start: number; end: number };
  }>({});
  const lastPdfUrlCursorPositions = useRef<{
    [key: number]: { start: number; end: number };
  }>({});

  useEffect(() => {
    if (firstDataRow && evalConfigs[0]?.promptTemplate) {
      const promptFormat = evalConfigs[0]?.promptFormat || 'text';
      if (promptFormat === 'text') {
        const updatedPrompt = getPopulatedPrompt(evalConfigs[0].promptTemplate, firstDataRow);
        setTestPrompt(updatedPrompt);
      } else {
        try {
          const populatedJson = getPopulatedJsonPrompt(evalConfigs[0].promptTemplate, firstDataRow);
          setTestPrompt(JSON.stringify(populatedJson[0] || {}, null, 2));
        } catch (e) {
          setTestPrompt(DEFAULT_TEST_PROMPT);
        }
      }
    } else {
      setTestPrompt(DEFAULT_TEST_PROMPT);
    }
  }, [evalConfigs, firstDataRow]);

  const handleVariableClickAbovePrompt = (variable: string, evalIndex: number) => {
    const variableText = `{{${variable}}}`;
    const savedPosition = lastPromptTextCursorPositions.current[evalIndex];

    // Calculate new cursor position before state update
    let newCursorPos = 0;
    if (savedPosition) {
      newCursorPos = savedPosition.start + variableText.length;
    } else {
      // If no saved position, cursor will be at end
      const currentValue = evalConfigs[evalIndex].promptTemplate;
      newCursorPos = currentValue.length + variableText.length;
    }

    const newEvalConfigs = produce(evalConfigs, draft => {
      const currentValue = draft[evalIndex].promptTemplate;

      if (savedPosition) {
        // Insert at saved cursor position
        draft[evalIndex].promptTemplate =
          currentValue.substring(0, savedPosition.start) +
          variableText +
          currentValue.substring(savedPosition.end);
      } else {
        // Fallback: append to end
        draft[evalIndex].promptTemplate += variableText;
      }
    });

    setEvalConfigs(newEvalConfigs);
    lastPromptTextCursorPositions.current[evalIndex] = {
      start: newCursorPos,
      end: newCursorPos,
    };

    if (hasPreviewRun[evalIndex]) {
      setNeedsRerunPreview(prev => ({ ...prev, [evalIndex]: true }));
    }
  };
  const handleVariableClickAboveImageAttachmentUrls = (variable: string, evalIndex: number) => {
    const variableText = `{{${variable}}}`;
    const savedPosition = lastImageUrlCursorPositions.current[evalIndex];

    // Calculate new cursor position before state update
    let newCursorPos = 0;
    if (savedPosition) {
      newCursorPos = savedPosition.start + variableText.length;
    } else {
      // If no saved position, cursor will be at end
      const currentValue = evalConfigs[evalIndex].imageAttachmentUrls || '';
      newCursorPos = currentValue.length + variableText.length;
    }

    const newEvalConfigs = produce(evalConfigs, draft => {
      const currentValue = draft[evalIndex].imageAttachmentUrls || '';

      if (savedPosition) {
        // Insert at saved cursor position
        draft[evalIndex].imageAttachmentUrls =
          currentValue.substring(0, savedPosition.start) +
          variableText +
          currentValue.substring(savedPosition.end);
      } else {
        // Fallback: append to end
        draft[evalIndex].imageAttachmentUrls = currentValue + variableText;
      }
    });

    setEvalConfigs(newEvalConfigs);
    lastImageUrlCursorPositions.current[evalIndex] = {
      start: newCursorPos,
      end: newCursorPos,
    };

    if (hasPreviewRun[evalIndex]) {
      setNeedsRerunPreview(prev => ({ ...prev, [evalIndex]: true }));
    }
  };
  const handleVariableClickAboveAudioAttachmentUrls = (variable: string, evalIndex: number) => {
    const variableText = `{{${variable}}}`;
    const savedPosition = lastAudioUrlCursorPositions.current[evalIndex];

    // Calculate new cursor position before state update
    let newCursorPos = 0;
    if (savedPosition) {
      newCursorPos = savedPosition.start + variableText.length;
    } else {
      // If no saved position, cursor will be at end
      const currentValue = evalConfigs[evalIndex].audioAttachmentUrls || '';
      newCursorPos = currentValue.length + variableText.length;
    }

    const newEvalConfigs = produce(evalConfigs, draft => {
      const currentValue = draft[evalIndex].audioAttachmentUrls || '';

      if (savedPosition) {
        // Insert at saved cursor position
        draft[evalIndex].audioAttachmentUrls =
          currentValue.substring(0, savedPosition.start) +
          variableText +
          currentValue.substring(savedPosition.end);
      } else {
        // Fallback: append to end
        draft[evalIndex].audioAttachmentUrls = currentValue + variableText;
      }
    });

    setEvalConfigs(newEvalConfigs);
    lastAudioUrlCursorPositions.current[evalIndex] = {
      start: newCursorPos,
      end: newCursorPos,
    };

    if (hasPreviewRun[evalIndex]) {
      setNeedsRerunPreview(prev => ({ ...prev, [evalIndex]: true }));
    }
  };

  const handleVariableClickAbovePdfAttachmentUrls = (variable: string, evalIndex: number) => {
    const variableText = `{{${variable}}}`;
    const savedPosition = lastPdfUrlCursorPositions.current[evalIndex];

    // Calculate new cursor position before state update
    let newCursorPos = 0;
    if (savedPosition) {
      newCursorPos = savedPosition.start + variableText.length;
    } else {
      // If no saved position, cursor will be at end
      const currentValue = evalConfigs[evalIndex].pdfAttachmentUrls || '';
      newCursorPos = currentValue.length + variableText.length;
    }

    const newEvalConfigs = produce(evalConfigs, draft => {
      const currentValue = draft[evalIndex].pdfAttachmentUrls || '';

      if (savedPosition) {
        // Insert at saved cursor position
        draft[evalIndex].pdfAttachmentUrls =
          currentValue.substring(0, savedPosition.start) +
          variableText +
          currentValue.substring(savedPosition.end);
      } else {
        // Fallback: append to end
        draft[evalIndex].pdfAttachmentUrls = currentValue + variableText;
      }
    });

    setEvalConfigs(newEvalConfigs);
    lastPdfUrlCursorPositions.current[evalIndex] = {
      start: newCursorPos,
      end: newCursorPos,
    };

    if (hasPreviewRun[evalIndex]) {
      setNeedsRerunPreview(prev => ({ ...prev, [evalIndex]: true }));
    }
  };

  const handleModelConfigChange = (
    value: Array<ChatApiEndpointWithId | ProjectChatApiEndpointWithId>,
    evalIndex: number,
  ) => {
    const newEvalConfigs = produce(evalConfigs, draft => {
      draft[evalIndex].modelConfigs = value as ChatApiEndpointWithId[];
    });
    setEvalConfigs(newEvalConfigs);
    if (hasPreviewRun[evalIndex]) {
      setNeedsRerunPreview(prev => ({ ...prev, [evalIndex]: true }));
    }
  };

  const handlePromptTemplateChange = (value: string, evalIndex: number) => {
    const newEvalConfigs = produce(evalConfigs, draft => {
      draft[evalIndex].promptTemplate = value;
    });
    setEvalConfigs(newEvalConfigs);
    if (hasPreviewRun[evalIndex]) {
      setNeedsRerunPreview(prev => ({ ...prev, [evalIndex]: true }));
    }
  };

  const handlePromptFormatChange = (value: string, evalIndex: number) => {
    const newEvalConfigs = produce(evalConfigs, draft => {
      const currentFormat = draft[evalIndex].promptFormat || 'text';
      draft[evalIndex].promptFormat = value as 'text' | 'json';

      if (value === 'json' && !draft[evalIndex].promptTemplate) {
        // Switching to JSON and no template exists - set example
        draft[evalIndex].promptTemplate = JSON_PROMPT_EXAMPLE;
      } else if (value === 'text' && currentFormat === 'json') {
        // Switching from JSON to text - clear the JSON content
        draft[evalIndex].promptTemplate = '';
      }
    });
    setEvalConfigs(newEvalConfigs);
    if (hasPreviewRun[evalIndex]) {
      setNeedsRerunPreview(prev => ({ ...prev, [evalIndex]: true }));
    }
  };

  const handleOutputColumnChange = (value: string, evalIndex: number) => {
    const newEvalConfigs = produce(evalConfigs, draft => {
      draft[evalIndex].outputColumn = value;
    });
    setEvalConfigs(newEvalConfigs);
    if (hasPreviewRun[evalIndex]) {
      setNeedsRerunPreview(prev => ({ ...prev, [evalIndex]: true }));
    }
  };

  const handleChangeTimeout = (value: string, evalIndex: number) => {
    if (value === '') {
      return;
    }

    const newEvalConfigs = produce(evalConfigs, draft => {
      draft[evalIndex].timeoutSeconds = +value;
    });
    setEvalConfigs(newEvalConfigs);
  };

  const handleChangeRetries = (value: string, evalIndex: number) => {
    if (value === '') {
      return;
    }

    const newEvalConfigs = produce(evalConfigs, draft => {
      draft[evalIndex].numRetries = +value;
    });
    setEvalConfigs(newEvalConfigs);
  };

  const handleImageAttachmentChange = (checked: boolean, evalIndex: number) => {
    const newEvalConfigs = produce(evalConfigs, draft => {
      draft[evalIndex].includeImageAttachments = checked;
    });
    setEvalConfigs(newEvalConfigs);
    if (hasPreviewRun[evalIndex]) {
      setNeedsRerunPreview(prev => ({ ...prev, [evalIndex]: true }));
    }
  };

  const handleImageUrlsChange = (urls: string, evalIndex: number) => {
    const newEvalConfigs = produce(evalConfigs, draft => {
      draft[evalIndex].imageAttachmentUrls = urls;
    });
    setEvalConfigs(newEvalConfigs);
    if (hasPreviewRun[evalIndex]) {
      setNeedsRerunPreview(prev => ({ ...prev, [evalIndex]: true }));
    }
  };

  const handleAudioAttachmentChange = (checked: boolean, evalIndex: number) => {
    const newEvalConfigs = produce(evalConfigs, draft => {
      draft[evalIndex].includeAudioAttachments = checked;
    });
    setEvalConfigs(newEvalConfigs);
    if (hasPreviewRun[evalIndex]) {
      setNeedsRerunPreview(prev => ({ ...prev, [evalIndex]: true }));
    }
  };

  const handleAudioUrlsChange = (urls: string, evalIndex: number) => {
    const newEvalConfigs = produce(evalConfigs, draft => {
      draft[evalIndex].audioAttachmentUrls = urls;
    });
    setEvalConfigs(newEvalConfigs);
    if (hasPreviewRun[evalIndex]) {
      setNeedsRerunPreview(prev => ({ ...prev, [evalIndex]: true }));
    }
  };

  const handlePdfAttachmentChange = (checked: boolean, evalIndex: number) => {
    const newEvalConfigs = produce(evalConfigs, draft => {
      draft[evalIndex].includePdfAttachments = checked;
    });
    setEvalConfigs(newEvalConfigs);
    if (hasPreviewRun[evalIndex]) {
      setNeedsRerunPreview(prev => ({ ...prev, [evalIndex]: true }));
    }
  };

  const handlePdfUrlsChange = (urls: string, evalIndex: number) => {
    const newEvalConfigs = produce(evalConfigs, draft => {
      draft[evalIndex].pdfAttachmentUrls = urls;
    });
    setEvalConfigs(newEvalConfigs);
    if (hasPreviewRun[evalIndex]) {
      setNeedsRerunPreview(prev => ({ ...prev, [evalIndex]: true }));
    }
  };

  const handleAddEval = () => {
    const newEvalConfigs = produce(evalConfigs, draft => {
      const evalNumber = draft.length + 1;
      draft.push({
        modelConfigs: DEFAULT_ENDPOINTS,
        promptTemplate: '',
        outputColumn: `output_${evalNumber}`,
        includeImageAttachments: false,
        imageAttachmentUrls: '',
      });
    });
    setEvalConfigs(newEvalConfigs);
  };

  const handleRemoveEval = (evalIndex: number) => {
    const newEvalConfigs = produce(evalConfigs, draft => {
      draft.splice(evalIndex, 1);
    });
    setEvalConfigs(newEvalConfigs);
    setPreviewResults(prev => {
      const updated = { ...prev };
      delete updated[evalIndex];
      return updated;
    });
    setNeedsRerunPreview(prev => {
      const updated = { ...prev };
      delete updated[evalIndex];
      return updated;
    });
    setHasPreviewRun(prev => {
      const updated = { ...prev };
      delete updated[evalIndex];
      return updated;
    });
    setIsLoading(prev => {
      const updated = { ...prev };
      delete updated[evalIndex];
      return updated;
    });
    setIsPreviewVisible(prev => {
      const updated = { ...prev };
      delete updated[evalIndex];
      return updated;
    });
  };

  const sendPromptToModel = async (
    prompt: string | Message[],
    modelConfig: ChatApiEndpointWithId,
    attachments: any[] = [],
  ) => {
    try {
      if (modelConfig.params.sampleModelParams) {
        throw new Error(
          'sampleModelParams is not supported yet. Please use the Chat Task Preview.',
        );
      }

      // Handle both text and JSON message formats
      let testMessage;
      if (typeof prompt === 'string') {
        testMessage = [
          {
            role: MessageRole.User,
            content: prompt,
            attachments,
          },
        ];
      } else {
        // For JSON format, add attachments to the first user message
        testMessage = prompt.map((message, index) => {
          if (message.role === MessageRole.User && index === 0) {
            return {
              ...message,
              attachments,
            };
          }
          return message;
        });
      }

      const endpointDetails = masterEndpointMap[modelConfig.url];
      if (!endpointDetails) {
        throw new Error(`Endpoint not found with url: ${modelConfig.url}`);
      }

      const strategyName = endpointDetails.strategy;
      const parsedParams = _.omitBy(modelConfig.params, _.isNull);

      try {
        StrategyParamsSchemaMap[strategyName].parse(parsedParams);
      } catch (e: any) {
        throw new Error(`${e.issues[0].path}: ${e.issues[0].message}`);
      }

      const body: CreateCompletionRequest<typeof strategyName> = {
        strategyName,
        params: parsedParams,
        messages: testMessage,
        projectId: projectId || null,
        useCase: 'compass-model-eval-previews',
      };

      // Handle streaming responses using SSE (Server-Sent Events)
      if (parsedParams.stream) {
        return new Promise<string>((resolve, reject) => {
          let responseText = '';

          apiFetch(null, 'internal/self_serve/rlhf/createCompletion', {
            method: 'POST',
            body,
            fetchEventSourceOptions: {
              openWhenHidden: true,
              onopen: async response => {
                if (!response.ok) {
                  reject(
                    new Error(
                      'Failed to get model response. Please try again. Response: ' +
                        JSON.stringify(response || {}),
                    ),
                  );
                }
              },
              onmessage: event => {
                if (event.data === '[DONE]' || event.data === '[IGNORE]') {
                  return;
                }
                try {
                  const parsed = JSON.parse(event.data);
                  if (parsed.error) {
                    reject(new Error(parsed.error));
                    return;
                  }
                  // Accumulate content from streaming chunks
                  // Follows the same pattern as useChatCompletion.ts
                  if (parsed.fullOutput) {
                    responseText = parsed.fullOutput;
                  } else if (parsed.output) {
                    responseText += parsed.output;
                  } else if (parsed.choices?.[0]?.delta?.content) {
                    responseText += parsed.choices[0].delta.content as string;
                  }
                } catch (parseError) {
                  // Skip malformed chunks, continue accumulating
                }
              },
              onclose: () => {
                resolve(responseText);
              },
              onerror: e => {
                reject(new Error(e?.message || 'Streaming error occurred'));
              },
            },
          }).catch(reject);
        });
      }

      // Non-streaming response handling
      const response = await apiFetch<CreateCompletionResponse>(
        null,
        'internal/self_serve/rlhf/createCompletion',
        {
          method: 'POST',
          body,
        },
      );

      if (response.error) {
        throw new Error(`Model error (${response.error})`);
      }

      return response.choices[0].content;
    } catch (e: any) {
      throw new Error(e.message || 'An error occurred during the API call.');
    }
  };

  const handlePreview = async (evalIndex: number) => {
    const config = evalConfigs[evalIndex];
    const promptTemplate = config.promptTemplate || DEFAULT_TEST_PROMPT;
    const promptFormat = config.promptFormat || 'text';

    let promptToSend: string | Message[];

    if (promptFormat === 'text') {
      promptToSend = firstDataRow ? getPopulatedPrompt(promptTemplate, firstDataRow) : testPrompt;
    } else {
      try {
        // First validate the JSON
        const validation = validateJsonPrompt(promptTemplate);
        if (!validation.valid) {
          alert(`Invalid JSON format: ${validation.error}`);
          return;
        }

        // Then populate with variables
        if (firstDataRow) {
          promptToSend = getPopulatedJsonPrompt(promptTemplate, firstDataRow);
        } else {
          promptToSend = JSON.parse(promptTemplate);
        }
      } catch (e: any) {
        alert(`Error parsing JSON: ${e.message}`);
        return;
      }
    }

    if (!config.modelConfigs.length) {
      alert('Please select at least one model configuration.');
      return;
    }

    setIsLoading(prev => ({ ...prev, [evalIndex]: true }));

    try {
      const shouldAppendModelId = config.modelConfigs.length > 1;

      const attachments: any[] = [];

      if (config.includeImageAttachments && config.imageAttachmentUrls) {
        const imageUrls = getPopulatedPromptVariablesAsArray(
          config.imageAttachmentUrls,
          firstDataRow,
        );

        if (imageUrls && imageUrls.length > 0) {
          const imageAttachments = imageUrls.map(url => {
            return {
              url,
              fileType: ModelInputFileAttachmentType.Image,
              mimeType: 'image/jpeg',
            };
          });
          attachments.push(...imageAttachments);
        }
      }

      if (config.includeAudioAttachments && config.audioAttachmentUrls) {
        const audioUrls = getPopulatedPromptVariablesAsArray(
          config.audioAttachmentUrls,
          firstDataRow,
        );

        if (audioUrls && audioUrls.length > 0) {
          const audioAttachments = audioUrls.map(url => {
            return {
              url,
              fileType: ModelInputFileAttachmentType.Audio,
              mimeType: 'audio/wav',
            };
          });
          attachments.push(...audioAttachments);
        }
      }

      if (config.includePdfAttachments && config.pdfAttachmentUrls) {
        const pdfUrls = getPopulatedPromptVariablesAsArray(config.pdfAttachmentUrls, firstDataRow);

        if (pdfUrls && pdfUrls.length > 0) {
          const pdfAttachments = pdfUrls.map(url => {
            return {
              url,
              fileType: ModelInputFileAttachmentType.Pdf,
              mimeType: 'application/pdf',
            };
          });
          attachments.push(...pdfAttachments);
        }
      }

      const responses = await Promise.all(
        config.modelConfigs.map(async modelConfig => {
          const updatedModelConfig = _.cloneDeep(modelConfig);
          if (firstDataRow) {
            const populatedParams = await populateTemplateHelper(
              updatedModelConfig.params,
              firstDataRow,
              { strict: true },
            );
            updatedModelConfig.params = populatedParams;
          }

          const response = await sendPromptToModel(promptToSend, updatedModelConfig, attachments);
          return {
            outputColumn: shouldAppendModelId
              ? `${config.outputColumn}_${modelConfig.id}`
              : config.outputColumn,
            modelResponse: response,
          };
        }),
      );

      setPreviewResults(prev => ({
        ...prev,
        [evalIndex]: {
          responses,
          prompt:
            typeof promptToSend === 'string' ? promptToSend : JSON.stringify(promptToSend, null, 2),
        },
      }));
      setNeedsRerunPreview(prev => ({ ...prev, [evalIndex]: false }));
      setHasPreviewRun(prev => ({ ...prev, [evalIndex]: true }));
      setIsPreviewVisible(prev => ({ ...prev, [evalIndex]: true }));
    } catch (error: any) {
      alert(`Error during preview: ${error.message}`);
    } finally {
      setIsLoading(prev => ({ ...prev, [evalIndex]: false }));
    }
  };

  const handleHidePreview = (evalIndex: number) => {
    setIsPreviewVisible(prev => ({ ...prev, [evalIndex]: false }));
  };

  const VariableBadges: React.FC<{
    index: number;
    usedVariables: string[];
    handleVariableClick: (variable: string, index: number) => void;
  }> = ({ index, usedVariables, handleVariableClick }) => {
    const [showAll, setShowAll] = useState(false);

    return (
      <div className="flex flex-wrap gap-2 self-start mt-2">
        {(showAll ? availableVariables : availableVariables.slice(0, 20)).map(variable => (
          <Badge
            size="1"
            color={usedVariables.includes(variable) ? 'accent' : 'neutral'}
            key={variable}
            onClick={() => handleVariableClick(variable, index)}
            className="cursor-pointer"
          >
            {variable}
          </Badge>
        ))}
        {availableVariables.length > 20 && !showAll && (
          <Link onClick={() => setShowAll(true)}>
            Show additional {availableVariables.length - 20} columns
          </Link>
        )}
        {availableVariables.length > 20 && showAll && (
          <Link onClick={() => setShowAll(false)}>Show fewer</Link>
        )}
      </div>
    );
  };

  const renderPromptConfiguration = (config: EvalConfig, index: number) => {
    const promptFormat = config.promptFormat || 'text';
    const usedPromptVariables =
      promptFormat === 'text'
        ? (config.promptTemplate.match(/{{(.*?)}}/g) || []).map((match: string) =>
            match.slice(2, -2),
          )
        : extractVariablesFromJson(config.promptTemplate);

    const usedImageAttachmentVariables =
      config.includeImageAttachments !== undefined &&
      config.includeImageAttachments &&
      config.imageAttachmentUrls !== undefined
        ? (config.imageAttachmentUrls.match(/{{(.*?)}}/g) || []).map((match: string) =>
            match.slice(2, -2),
          )
        : [];

    const usedAudioAttachmentVariables =
      config.includeAudioAttachments !== undefined &&
      config.includeAudioAttachments &&
      config.audioAttachmentUrls !== undefined
        ? (config.audioAttachmentUrls.match(/{{(.*?)}}/g) || []).map((match: string) =>
            match.slice(2, -2),
          )
        : [];

    const usedPdfAttachmentVariables =
      config.includePdfAttachments !== undefined &&
      config.includePdfAttachments &&
      config.pdfAttachmentUrls !== undefined
        ? (config.pdfAttachmentUrls.match(/{{(.*?)}}/g) || []).map((match: string) =>
            match.slice(2, -2),
          )
        : [];

    return (
      <>
        {/* Prompt Template */}
        <Typography.Subtitle3>Prompt Template</Typography.Subtitle3>

        {/* Format Toggle */}
        <div className="mt-2 mb-4">
          <SegmentedControl.Root
            value={promptFormat}
            onValueChange={value => handlePromptFormatChange(value, index)}
          >
            <SegmentedControl.Item value="text">Text Format</SegmentedControl.Item>
            <SegmentedControl.Item value="json">JSON Format</SegmentedControl.Item>
          </SegmentedControl.Root>
        </div>

        {/* Variable Badges for Prompt Template */}
        <VariableBadges
          index={index}
          usedVariables={usedPromptVariables}
          handleVariableClick={handleVariableClickAbovePrompt}
        />

        <TextArea
          className="w-full mt-2 mb-4"
          id={`promptTemplate-${index}`}
          value={config.promptTemplate}
          placeholder={
            promptFormat === 'json'
              ? 'Enter JSON array of messages. Example: [{"role": "system", "content": "{{context}}"}, {"role": "user", "content": "{{question}}"}]'
              : 'Click any variable above to insert it into the prompt template.'
          }
          disabled={false}
          required={true}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            handlePromptTemplateChange(e.target.value, index)
          }
          error={hasError && !config.promptTemplate}
          onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
            // Save cursor position when TextArea loses focus
            lastPromptTextCursorPositions.current[index] = {
              start: e.target.selectionStart,
              end: e.target.selectionEnd,
            };
          }}
        />

        {/* Image Attachment Checkbox */}
        <div className="mb-2">
          <Checkbox
            checked={config.includeImageAttachments || false}
            onCheckedChange={(checked: boolean) => {
              handleImageAttachmentChange(checked, index);
            }}
          />
          <Text className="ml-2">Add image attachment(s)</Text>
        </div>

        {/* Variable Badges for Image Attachment */}
        {config.includeImageAttachments && (
          <VariableBadges
            index={index}
            usedVariables={usedImageAttachmentVariables}
            handleVariableClick={handleVariableClickAboveImageAttachmentUrls}
          />
        )}

        {/* Image URL Text Field */}
        {config.includeImageAttachments && (
          <TextField
            className="w-full mt-2 mb-4"
            id={`imageUrls-${index}`}
            value={config.imageAttachmentUrls || ''}
            placeholder="Enter image URLs"
            disabled={false}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleImageUrlsChange(e.target.value, index);
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              // Save cursor position when TextField loses focus
              lastImageUrlCursorPositions.current[index] = {
                start: e.target.selectionStart || 0,
                end: e.target.selectionEnd || 0,
              };
            }}
          />
        )}

        {/* Audio Attachment Checkbox */}
        <div className="mb-2">
          <Checkbox
            checked={config.includeAudioAttachments || false}
            onCheckedChange={(checked: boolean) => {
              handleAudioAttachmentChange(checked, index);
            }}
          />
          <Text className="ml-2">Add audio attachment(s)</Text>
        </div>

        {/* Variable Badges for Audio Attachment */}
        {config.includeAudioAttachments && (
          <VariableBadges
            index={index}
            usedVariables={usedAudioAttachmentVariables}
            handleVariableClick={handleVariableClickAboveAudioAttachmentUrls}
          />
        )}

        {/* Audio URL Text Field */}
        {config.includeAudioAttachments && (
          <TextField
            className="w-full mt-2 mb-4"
            id={`audioUrls-${index}`}
            value={config.audioAttachmentUrls || ''}
            placeholder="Enter audio URLs"
            disabled={false}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleAudioUrlsChange(e.target.value, index);
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              // Save cursor position when TextField loses focus
              lastAudioUrlCursorPositions.current[index] = {
                start: e.target.selectionStart || 0,
                end: e.target.selectionEnd || 0,
              };
            }}
          />
        )}

        {/* PDF Attachment Checkbox */}
        <div className="mb-2">
          <Checkbox
            checked={config.includePdfAttachments || false}
            onCheckedChange={(checked: boolean) => {
              handlePdfAttachmentChange(checked, index);
            }}
          />
          <Text className="ml-2">Add PDF attachment(s)</Text>
        </div>

        {/* Variable Badges for PDF Attachment */}
        {config.includePdfAttachments && (
          <VariableBadges
            index={index}
            usedVariables={usedPdfAttachmentVariables}
            handleVariableClick={handleVariableClickAbovePdfAttachmentUrls}
          />
        )}

        {/* PDF URL Text Field */}
        {config.includePdfAttachments && (
          <TextField
            className="w-full mt-2 mb-4"
            id={`pdfUrls-${index}`}
            value={config.pdfAttachmentUrls || ''}
            placeholder="Enter PDF URLs"
            disabled={false}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handlePdfUrlsChange(e.target.value, index);
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              // Save cursor position when TextField loses focus
              lastPdfUrlCursorPositions.current[index] = {
                start: e.target.selectionStart || 0,
                end: e.target.selectionEnd || 0,
              };
            }}
          />
        )}
      </>
    );
  };

  return (
    <div className="flex flex-col">
      <CompassInstructions
        header="How to use the Model Evaluation card"
        storageKey="modelEvalCardInstructionsExpanded"
        icon={faBookOpen}
      >
        <Callout.Text>
          <Text as="p">
            This card allows you to run model evaluations on your data using any supported model. To
            run a model evaluation:
          </Text>

          <Text as="p" className="mt-2">
            <Strong>1. Enter a project ID:</Strong> Provide a project ID for cost-attribution
            purposes. This is required to run the evaluation job.
          </Text>

          <Text as="p" className="mt-2">
            <Strong>2. Configure prompts:</Strong> Create prompt templates using variables from your
            data. Click on any variable to insert it into your prompt template using the{' '}
            <RadixCode>{'{{variable}}'}</RadixCode> syntax. You can use either simple text prompts
            or JSON format for multi-turn conversations*.
          </Text>

          <Text as="p" className="mt-2">
            <Strong>3. (Optional) Configure image attachments:</Strong> If desired, add image
            attachments to your prompt template. Check the 'Add image attachment(s)' checkbox. Click
            on any variable to insert it into your image attachment using the{' '}
            <RadixCode>{'{{variable}}'}</RadixCode> syntax. The image attachment text box must only
            contain the variables of the URLs you want to attach, and the model strategy must be
            LiteLLM.
          </Text>

          <Text as="p" className="mt-2">
            <Strong>3. (Optional) Configure audio attachments:</Strong> If desired, add audio
            attachments to your prompt template. Check the 'Add audio attachment(s)' checkbox. Click
            on any variable to insert it into your audio attachment using the{' '}
            <RadixCode>{'{{variable}}'}</RadixCode> syntax. The audio attachment text box must only
            contain the variables of the URLs you want to attach, and the model strategy must be
            LiteLLM.
          </Text>

          <Text as="p" className="mt-2">
            <Strong>3. (Optional) Configure PDF attachments:</Strong> If desired, add PDF
            attachments to your prompt template. Check the 'Add PDF attachment(s)' checkbox. Click
            on any variable to insert it into your PDF attachment using the{' '}
            <RadixCode>{'{{variable}}'}</RadixCode> syntax. The PDF attachment text box must only
            contain the variables of the URLs you want to attach, and the model strategy must be
            LiteLLM.
          </Text>

          <Text as="p" className="mt-2">
            <Strong>4. Select models:</Strong> Choose one or more models to evaluate. You can
            compare multiple models with the same prompt by adding multiple model configurations.
            You can also insert variables in model configs using the{' '}
            <RadixCode>{'{{variable}}'}</RadixCode> syntax.
          </Text>

          <Text as="p" className="mt-2">
            <Strong>5. Define output columns:</Strong> Specify column names for the model responses.
            If you're using multiple models, the model ID will be appended to the column name.
          </Text>

          <Text as="p" className="mt-2">
            <Strong>6. Preview results:</Strong> Test your configuration on a sample row before
            running the full evaluation job.
          </Text>

          <Text as="p" className="mt-2">
            <Em>
              *Variables from your table are only available as shown in the previous table. For JSON
              format, use an array of message objects with 'role' and 'content' properties.
            </Em>
          </Text>
        </Callout.Text>
      </CompassInstructions>

      {/* Global Inputs */}
      <div style={{ display: 'flex', alignItems: 'center' }} className="mt-3 gap-1">
        <Typography.Subtitle3>Project ID</Typography.Subtitle3>
        <RadixTooltip content={<Text>This is required for cost-attribution purposes.</Text>}>
          <RadixIcon label="tooltip" icon={faInfoCircle} size="1" />
        </RadixTooltip>
      </div>
      <TextField
        className="w-full pt-2 pb-4"
        id="projectId"
        value={projectId}
        disabled={false}
        required={true}
        error={hasError && !projectId}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProjectId(e.target.value)}
      />

      {/* Evaluations */}
      <div style={{ display: 'flex', alignItems: 'center' }} className="mt-3 gap-1">
        <Typography.Subtitle3>Evaluations</Typography.Subtitle3>
        <RadixTooltip content={<Text>Add an evaluation for each prompt you want to run.</Text>}>
          <RadixIcon label="tooltip" icon={faInfoCircle} size="1" />
        </RadixTooltip>
      </div>
      <div className="flex flex-col gap-2">
        {
          // eslint-disable-next-line complexity
          evalConfigs.map((config, i) => {
            return (
              <div className="flex pt-2" key={i}>
                <div className="flex w-full items-start">
                  {/* Evaluation Inputs */}
                  <div
                    className={`p-4 border rounded-md border-primary-800 ${
                      isPreviewVisible[i] ? 'w-1/2' : 'w-full'
                    }`}
                  >
                    {renderPromptConfiguration(config, i)}

                    {/* Model Configuration */}
                    <ChatApiEndpointsBuilder
                      name="Model Configuration"
                      description="Please be conscious of cost when choosing your model(s)."
                      existingValue={config.modelConfigs}
                      required={true}
                      updateProperty={(
                        value: Array<ChatApiEndpointWithId | ProjectChatApiEndpointWithId>,
                      ) => handleModelConfigChange(value, i)}
                      customTestPrompt={testPrompt}
                      maxNumberOfEndpoints={5}
                      showTestButton={false}
                    />

                    {/* Output Column */}
                    <div className="mt-4 gap-1" style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography.Subtitle3>Output Column</Typography.Subtitle3>
                      <RadixTooltip
                        content={
                          <Text>
                            If you are evaluating against multiple models, the model id will be
                            appended to the column name.
                          </Text>
                        }
                      >
                        <RadixIcon label="tooltip" icon={faInfoCircle} size="1" />
                      </RadixTooltip>
                    </div>
                    <TextField
                      className="w-full pt-2"
                      id={`outputColumn-${i}`}
                      value={config.outputColumn}
                      disabled={false}
                      required={true}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleOutputColumnChange(e.target.value, i)
                      }
                      error={hasError && !config.outputColumn}
                    />

                    {/* Timeout */}
                    <div className="mt-4 gap-1" style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography.Subtitle3>Timeout Seconds</Typography.Subtitle3>
                      <RadixTooltip
                        content={
                          <Text>
                            How long to wait for the model to respond to a single prompt. If the
                            model does not respond within this time, the evaluation for just that
                            single prompt will be cancelled. By default, this is set to{' '}
                            {MODEL_EVAL_DEFAULT_TIMEOUT_SECONDS} seconds.
                            <br />
                            <br />
                            - For reasoning models, we recommend setting this to at most 900 seconds
                            (15 minutes).
                            <br />- For non-reasoning models, we recommend setting this to at most
                            300 seconds (5 minutes).
                          </Text>
                        }
                      >
                        <RadixIcon label="tooltip" icon={faInfoCircle} size="1" />
                      </RadixTooltip>
                    </div>
                    <TextField
                      className="w-full pt-2"
                      id={`timeoutSeconds-${i}`}
                      value={
                        _.isNil(config.timeoutSeconds)
                          ? MODEL_EVAL_DEFAULT_TIMEOUT_SECONDS
                          : config.timeoutSeconds
                      }
                      type="number"
                      disabled={false}
                      required={false}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeTimeout(e.target.value, i)
                      }
                      min={1}
                      max={1800}
                      error={_.isNumber(config.timeoutSeconds) && config.timeoutSeconds <= 0}
                    />

                    {/* Num retries */}
                    <div className="mt-4 gap-1" style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography.Subtitle3>Num Retries</Typography.Subtitle3>
                      <RadixTooltip
                        content={
                          <Text>
                            For errors encountered for a single model call (e.g. model rate limits),
                            the evaluation will be retried this many times. By default, this is set
                            to {MODEL_EVAL_DEFAULT_NUM_RETRIES}.
                          </Text>
                        }
                      >
                        <RadixIcon label="tooltip" icon={faInfoCircle} size="1" />
                      </RadixTooltip>
                    </div>
                    <TextField
                      className="w-full pt-2"
                      id={`numRetries-${i}`}
                      value={
                        _.isNil(config.numRetries)
                          ? MODEL_EVAL_DEFAULT_NUM_RETRIES
                          : config.numRetries
                      }
                      type="number"
                      min={0}
                      max={5}
                      disabled={false}
                      required={false}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChangeRetries(e.target.value, i)
                      }
                      error={_.isNumber(config.numRetries) && config.numRetries < 0}
                    />

                    {/* Preview Button */}
                    {(!isPreviewVisible[i] || needsRerunPreview[i]) && (
                      <Button
                        variant="primary"
                        className="mt-4"
                        onClick={() => handlePreview(i)}
                        icon={{
                          position: 'left',
                          type: ['fas', 'arrow-right'],
                        }}
                        disabled={isLoading[i]}
                      >
                        {isLoading[i]
                          ? 'Loading...'
                          : hasPreviewRun[i] && needsRerunPreview[i]
                          ? 'Refresh Preview'
                          : 'Preview'}
                      </Button>
                    )}
                  </div>

                  {/* Arrow between columns */}
                  {isPreviewVisible[i] && (
                    <div className="flex items-center mx-2">
                      <BaseIcon icon={['fas', 'arrow-right']} size="lg" />
                    </div>
                  )}

                  {/* Preview Results */}
                  {isPreviewVisible[i] && previewResults[i] && (
                    <div
                      className="p-4 border rounded-md border-neutral-200 w-1/2"
                      style={{ animation: 'slide-in 0.5s forwards' }}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <Typography.Subtitle3>Preview Results</Typography.Subtitle3>
                        <Button variant="secondary" size="sm" onClick={() => handleHidePreview(i)}>
                          Hide Preview
                        </Button>
                      </div>

                      <div className="p-2 border rounded-md border-neutral-200">
                        <Typography.Subtitle3>Prompt</Typography.Subtitle3>
                        <Typography.Caption>{previewResults[i].prompt}</Typography.Caption>
                      </div>

                      {/* Preview Results Table */}
                      <Table
                        className="-highlight mt-4"
                        data={previewResults[i].responses}
                        columns={[
                          {
                            id: 'outputColumn',
                            header: 'Output Column',
                          },
                          {
                            id: 'modelResponse',
                            header: 'Model Response',
                          },
                        ]}
                        defaultPageSize={3}
                      />
                    </div>
                  )}

                  {/* Remove Evaluation Button */}
                  <Icon
                    className="ml-2 mt-2"
                    icon={['fas', 'minus']}
                    onClick={() => handleRemoveEval(i)}
                    rounded
                    size="sm"
                    tooltip="Remove evaluation"
                    variant="neutral"
                  />
                </div>
              </div>
            );
          })
        }
      </div>

      {/* Add Evaluation Button */}
      <div className="flex flex-row gap-2 mt-2">
        <Button
          className="my-2 p-2"
          variant="secondary"
          icon={{
            position: 'left',
            type: ['fas', 'plus'],
          }}
          title="Add Evaluation"
          onClick={handleAddEval}
        >
          Add Evaluation
        </Button>
      </div>

      <div className="flex flex-row gap-2 mt-2">
        <Flex align="center" justify="center">
          <Box>
            <Text className="cursor-pointer" as="label" size="2">
              <Flex as="span" gap="2" align="center">
                <Checkbox
                  checked={useCache}
                  onCheckedChange={() => {
                    setUseCache(!useCache);
                  }}
                />{' '}
                <strong>[Advanced]</strong> Enable model response caching{' '}
                <Flex justify="center">
                  <RadixTooltip content="Disabling response caching will force the model to generate a new response for each evaluation. Unless you are generating multiple evals on the same prompt, this option is not recommended.">
                    <RadixIcon label="Info" icon={faInfoCircle} size="1" />
                  </RadixTooltip>
                </Flex>
              </Flex>
            </Text>
          </Box>
        </Flex>
      </div>
    </div>
  );
};

export default ModelEvalCardComponent;
